// PayoutMethodsPage — Manage linked payout methods
// Bank accounts, crypto wallets, PayPal — for receiving payouts
// Layout: iOS list with verify badges, add new, default marker

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CountryFlag } from '../../components/Brand';

interface Method {
  id: string;
  type: 'bank' | 'crypto' | 'paypal' | 'venmo' | 'wise';
  label: string;
  detail: string;
  verified: boolean;
  isDefault?: boolean;
  icon: string;
  accent: string;
  country?: string;
}

const METHODS: Method[] = [
  { id: 'm1', type: 'bank',   label: 'Chase Bank',     detail: 'Checking • ••••4521', verified: true,  isDefault: true, icon: '🏦', accent: '#007AFF' },
  { id: 'm2', type: 'bank',   label: 'Bank of America', detail: 'Savings • ••••8932',  verified: true,                    icon: '🏦', accent: '#007AFF' },
  { id: 'm3', type: 'crypto', label: 'Coinbase USDC',   detail: '0x7C29…84F',          verified: true,                    icon: '🪙', accent: '#5856D6' },
  { id: 'm4', type: 'paypal', label: 'PayPal',          detail: 'brandon@email.com',   verified: true,                    icon: '🅿️', accent: '#007AFF' },
  { id: 'm5', type: 'wise',   label: 'Wise (GBP)',      detail: 'GB • ••••7231',       verified: false,                   icon: '🌍', accent: '#34C759', country: 'GB' },
];

export function PayoutMethodsPage() {
  const nav = useNavigate();
  const [methods, setMethods] = useState(METHODS);
  const [adding, setAdding] = useState(false);

  function setDefault(id: string) {
    setMethods((curr) => curr.map((m) => ({ ...m, isDefault: m.id === id })));
  }
  function remove(id: string) {
    if (confirm('Remove this payout method? Future payouts cannot be sent to it.')) {
      setMethods((curr) => curr.filter((m) => m.id !== id));
    }
  }

  return (
    <div className="min-h-screen bg-tf-surface-1 pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border ios26-navbar px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Payout methods</div>
        <button onClick={() => setAdding(true)} data-testid="add-method" className="px-3 text-[15px] font-semibold text-tf-blue">Add</button>
      </div>

      <div className="mx-4 mt-3">
        <div className="ios26-hero text-center">
          <div className="text-[11px] uppercase tracking-wider text-tf-text-3">Default method</div>
          <div className="mt-1 text-[20px] font-bold tracking-tight text-tf-text-1">
            {methods.find((m) => m.isDefault)?.label || 'None'}
          </div>
          <div className="mt-1 text-[12px] text-tf-text-3">{methods.filter((m) => m.verified).length} verified · {methods.length} total</div>
        </div>
      </div>

      {/* Banks */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Bank accounts</h2>
      <div className="mx-4 ios26-list bg-white">
        {methods.filter((m) => m.type === 'bank').map((m, i, a) => <MethodCard key={m.id} method={m} last={i === a.length - 1} onSetDefault={() => setDefault(m.id)} onRemove={() => remove(m.id)} />)}
      </div>

      {/* Crypto */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Crypto</h2>
      <div className="mx-4 ios26-list bg-white">
        {methods.filter((m) => m.type === 'crypto').length === 0 ? <Empty label="No crypto wallets" /> : methods.filter((m) => m.type === 'crypto').map((m, i, a) => <MethodCard key={m.id} method={m} last={i === a.length - 1} onSetDefault={() => setDefault(m.id)} onRemove={() => remove(m.id)} />)}
      </div>

      {/* Other */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Other</h2>
      <div className="mx-4 ios26-list bg-white">
        {methods.filter((m) => m.type === 'paypal' || m.type === 'wise' || m.type === 'venmo').map((m, i, a) => <MethodCard key={m.id} method={m} last={i === a.length - 1} onSetDefault={() => setDefault(m.id)} onRemove={() => remove(m.id)} />)}
      </div>

      <p className="mt-3 px-4 text-center text-[11px] text-tf-text-3">All methods are encrypted with bank-level security.</p>

      {/* Add method sheet */}
      {adding && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 backdrop-blur-sm" onClick={() => setAdding(false)}>
          <div className="ios26-sheet w-full max-w-[420px]" onClick={(e) => e.stopPropagation()}>
            <h2 className="px-4 pb-2 text-[20px] font-bold tracking-tight text-tf-text-1">Add payout method</h2>
            <p className="px-4 pb-3 text-[13px] text-tf-text-3">Choose how you want to receive funds.</p>
            <div className="space-y-1.5">
              {[
                { icon: '🏦', label: 'Bank account', sub: 'ACH or wire' },
                { icon: '🪙', label: 'Crypto wallet', sub: 'USDC, USDT, BTC' },
                { icon: '🅿️', label: 'PayPal', sub: 'Email or phone' },
                { icon: '🌍', label: 'Wise', sub: 'International transfers' },
                { icon: '🟦', label: 'Venmo', sub: 'US only' },
              ].map((o) => (
                <button key={o.label} onClick={() => { alert('Open ' + o.label + ' flow'); setAdding(false); }} className="flex w-full items-center gap-3 rounded-[12px] px-3 py-2.5 active:bg-tf-surface-1 transition">
                  <div className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-tf-blue/10 text-[18px]">{o.icon}</div>
                  <div className="flex-1 text-left">
                    <div className="text-[14px] font-semibold text-tf-text-1">{o.label}</div>
                    <div className="text-[11px] text-tf-text-3">{o.sub}</div>
                  </div>
                  <svg viewBox="0 0 24 24" className="h-4 w-4 text-tf-text-muted" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </button>
              ))}
            </div>
            <button onClick={() => setAdding(false)} className="mt-3 flex h-12 w-full items-center justify-center rounded-full bg-tf-surface-1 text-[15px] font-semibold text-tf-text-1 active:opacity-40">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function MethodCard({ method: m, last, onSetDefault, onRemove }: { method: Method; last?: boolean; onSetDefault: () => void; onRemove: () => void }) {
  return (
    <div data-testid={`method-${m.id}`} className={['px-4 py-3', last ? '' : 'border-b border-tf-border'].join(' ')}>
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-[10px] text-[18px]" style={{ backgroundColor: m.accent + '18' }}>{m.icon}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-[14px] font-semibold text-tf-text-1">{m.label}</span>
            {m.isDefault && <span className="rounded bg-tf-text-1 px-1 py-0.5 text-[8px] font-bold uppercase text-white">Default</span>}
            {m.country && <CountryFlag code={m.country} size={16} />}
          </div>
          <div className="text-[11px] text-tf-text-3">{m.detail}</div>
        </div>
        {m.verified ? (
          <span className="rounded-full bg-[#34C759]/15 px-1.5 py-0.5 text-[9px] font-bold uppercase text-tf-profit">✓ Verified</span>
        ) : (
          <button onClick={() => alert('Verify flow')} className="rounded-full bg-[#FF9500]/15 px-2 py-0.5 text-[10px] font-bold text-tf-warning">Verify</button>
        )}
      </div>
      <div className="mt-2 flex gap-2">
        {!m.isDefault && (
          <button onClick={onSetDefault} data-testid={`setdefault-${m.id}`} className="flex-1 h-8 rounded-full bg-tf-surface-1 text-[11px] font-semibold text-tf-text-1 active:opacity-40">Set as default</button>
        )}
        <button onClick={onRemove} data-testid={`remove-${m.id}`} className="flex-1 h-8 rounded-full bg-[#FF3B30]/10 text-[11px] font-semibold text-tf-loss active:opacity-40">Remove</button>
      </div>
    </div>
  );
}

function Empty({ label }: { label: string }) {
  return <div className="px-4 py-4 text-center text-[12px] text-tf-text-3">{label}</div>;
}
